import { replace } from 'connected-react-router';
import { createAction } from '@reduxjs/toolkit';
import { responseFailed } from './error';
import { navigateToScreen } from './navigation';
import {
  ROOT,
  CONFIRM_JOINT_LINK_CODE,
  YOUR_LOAN_OPTIONS,
  YOUR_FINANCIAL_CIRCUMSTANCES,
  YOUR_MAIN_BANK_ACCOUNT,
  REVIEW_YOUR_LOAN_DETAILS,
  CONGRATULATIONS,
  EMPLOYMENT_DETAILS
} from 'constants/paths';
import { resetIllustrationData, fetchAPRData } from 'actionCreators/calculator';
import { CONGRATS_REFERRER, EMPLOYMENT_REFERRER } from 'constants/general';
import { EXISTING_JOURNEY, JOINT_TYPE } from 'constants/general';
import { getCase } from './case';
import { showHideLoader } from './loader';

let configPath = '/config.json';

/**
 * @note webpack will remove this code block entirely if not building for production, hiding the existence
 * of `.stub.json` from the real world.
 */
if (process.env.NODE_ENV !== 'production') {
  if (process.env.REACT_APP_ENABLE_STUB === 'true') {
    configPath = '/config.stub.json';
  }
}

/**
 * @todo Stop treating fetching `config.json` as an "api" request.
 * Wrap the startup of the app within it and use it to initialise the store.
 * Doing so allows us to get rid of the need for any `isPaasApi` flag, which is
 * redundant. We should eventually be able to refactor away from this middleware stuff
 * and into adopting rtk-query patterns.
 * - KW 26/05/2024
 */
export const fetchConfig = createAction('config/fetchConfig', function prepare() {
  return {
    payload: {
      endPoint: configPath,
      method: 'get',
      isPaasApi: false,
      addHeader: false,
      success: fetchConfigSuccess,
      failure: responseFailed
    }
  };
});

export const fetchConfigSuccess = createAction(
  'config/fetchConfigSuccess',
  function prepare(contentUrls) {
    return {
      payload: contentUrls
    };
  }
);

export const setQueryParam = (payload) => (dispatch) => {
  dispatch({ type: 'setQueryParam', payload });
  let path;
  switch (payload.referrer) {
    case 'NCC': {
      path = YOUR_FINANCIAL_CIRCUMSTANCES;
      break;
    }
    case 'MBA': {
      path = YOUR_MAIN_BANK_ACCOUNT;
      break;
    }
    case 'RLD': {
      path = REVIEW_YOUR_LOAN_DETAILS;
      break;
    }
    case CONGRATS_REFERRER: {
      path = CONGRATULATIONS;
      break;
    }
    case EMPLOYMENT_REFERRER: {
      path = EMPLOYMENT_DETAILS;
      break;
    }
    default: {
      path = payload.path;
      break;
    }
  }

  path && dispatch(replace(path));
};

export const updateQueryParam = (param) => (dispatch) => {
  dispatch(setQueryParam(param));
  const { accessToken, bankId, jointLinkCode, isJoint } = param;
  //if token is to available it will redirect to first screen.This will help if user is refresh the page it will redirect to root path
  if (!accessToken && !isJoint) {
    if (process.env.REACT_APP_DISABLE_INITIAL_REDIRECT !== 'true') {
      // normal (production) behaviour, only disabled in local development
      dispatch(navigateToScreen(ROOT));
    }
  } else if (bankId && jointLinkCode && isJoint) {
    dispatch(updateJointBankId(bankId));
    dispatch(navigateToScreen(CONFIRM_JOINT_LINK_CODE));
  }
};

export const setUserJourney = createAction('setUserJourney', function prepare(journey) {
  return {
    payload: journey
  };
});

/**
 * Flag that the app is in "Post DIP Review" mode, with the user having submitted
 * and recived a Decision in Pinciple (DIP) Outcome, now having the option to edit some of their details,
 * which will require them to get another Decision in Principle before proceeding.
 * The @see CreateLoanScreen uses this as a signal that it should navigate the user to
 * the @see PostDIPReviewScreen instead of continuing on in the normal journey after editing their loan details.
 * @note currently only applicable to the New to Bank (NTB) journey.
 */
export const enablePostDIPReview = createAction('config/enablePostDIPReview');

/**
 * Disables "Post DIP Review" mode
 * @todo this might not be needed.
 */
export const disablePostDIPReview = createAction('config/disablePostDIPReview');

export const startAgain = createAction('startAgain', function prepare(isStartAgain) {
  return {
    payload: isStartAgain
  };
});

/**
 * Update the joint flag to identify the sole or joint journey
 */
export const setJointJourney = createAction('setJointJourney', function prepare(isJoint) {
  return {
    payload: isJoint
  };
});

/**
 * Update the secondary applicant flag to secondary applicant journey
 */
export const setSecondaryApplicant = createAction(
  'setSecondaryApplicant',
  function prepare(isSecondaryApplicant) {
    return {
      payload: isSecondaryApplicant
    };
  }
);

/**
 * Update referrer value to config to redirect on respective screen after Customer info screen
 */
export const updateReferrer = createAction('updateReferrer', function prepare(referrer) {
  return {
    payload: referrer
  };
});

/**
 * update native data to use in API calling like client context and user context
 */
export const updateNativeAppData = createAction(
  'updateNativeAppData',
  function prepare(nativeData) {
    return {
      payload: nativeData
    };
  }
);

/**
 * Convert Pre Assessed customer journey to existing customer journey and reset illustration data
 */
export const convertPreAssesedToExisting = () => (dispatch) => {
  dispatch(resetIllustrationData());
  dispatch(fetchAPRData());
  dispatch(startAgain(true));
  dispatch(setUserJourney(EXISTING_JOURNEY));
  dispatch(setJointJourney(JOINT_TYPE));
  dispatch(navigateToScreen(YOUR_LOAN_OPTIONS));
};

/**
 * Update loanType for the journey
 */
export const updateLoanType = createAction('updateLoanType', function prepare(loanType) {
  return {
    payload: loanType
  };
});

/**
 * Update values for primary applicant info as null once secondary applicant starts.
 */
export const resetPrimaryApplicantInfo = createAction('reset/resetPrimaryApplicantInfo');

/**
 * Update sendReferrerValue to config to call an getCase in joint secondary applicant flow
 */
export const sendReferrerValue = (referrer) => (dispatch, getState) => {
  const {
    config: { isSecondaryApplicant },
    caseDetails: { caseId }
  } = getState();

  if (
    referrer === 'RLD' ||
    (isSecondaryApplicant && caseId && referrer === 'MBA') ||
    referrer === EMPLOYMENT_REFERRER
  ) {
    dispatch(showHideLoader(true));
    dispatch(getCase(caseId));
  }
  dispatch(updateReferrer(referrer));
};

/**
 * Update primary applicant bank Id as jointBankId to verify whether secondary applicant has same bank Id
 */
export const updateJointBankId = createAction('updateJointBankId', function prepare(jointBankId) {
  return {
    payload: jointBankId
  };
});

export const enableDipToFullJourney = createAction('config/enableDipToFullJourney');

export const disableDipToFullJourney = createAction('config/disableDipToFullJourney');
